<script setup lang="ts">
import type { QueueItemSource } from '~/types'

import time from '~/utils/time'
import { generateTrackCreditString } from '~/utils/utils'
import { useStore } from '~/store'
import { getMany, useDataStore } from '~/data'

import Button from '~/ui/Button.vue'
import TrackFavoriteIcon from './favorites/TrackFavoriteIcon.vue'
import Sync from '~/ui/Sync.vue'

const store = useStore()

interface Events {
  (e: 'play', index: number): void
  (e: 'remove', index: number): void
}

interface Props {
  source: QueueItemSource
  index: number
}

defineEmits<Events>()
defineProps<Props>()
</script>

<template>
  <div
    :class="['queue-item interactive', 'color-default-solid', $style.item]"
    tabindex="0"
    ui-interactive
  >
    <div class="handle">
      <Icon icon="bi:list" />
    </div>
    <div
      class="image-cell"
      @click="$emit('play', index)"
    >
      <img
        class="ui mini image"
        alt=""
        :src="source.coverUrl"
      >
    </div>
    <div @click="$emit('play', index)">
      <div
        class="title reset ellipsis"
        :title="source.title"
        :aria-label="source.labels.selectTrack"
      >
        <strong>{{ source.title }}</strong><br>
        <Sync
          :use="() => getMany(
            useDataStore().get_artist, () => (source.artistCredit ?? []).map(({ artist: { id } }) => id)
          )"
          v-slot="{ value: { data: artists } }"
        >
          <span v-if="artists && source.artistCredit">
            {{ generateTrackCreditString(source.artistCredit, artists) }}
          </span>
        </Sync>
      </div>
    </div>
    <div class="duration-cell">
      <template v-if="source.sources.length > 0">
        {{ time.parse(Math.round(source.sources[0]?.duration ?? 0)) }}
      </template>
    </div>
    <div class="controls">
      <TrackFavoriteIcon
        v-if="store.state.auth.authenticated"
        :track="{ id: source.id }"
        :title="source.labels.favorite"
        :aria-label="source.labels.favorite"
        class="color-ghost shape-square"
      />
      <Button
        :aria-label="source.labels.remove"
        :title="source.labels.remove"
        ui-block-size="40"
        ui-overshoot-none
        class="color-default-ghost shape-square"
        @click.stop="$emit('remove', index)"
      >
        <Icon icon="bi:x" />
      </Button>
    </div>
  </div>
</template>

<style module>
.item {
  background-color: transparent;

  &:hover, &:focus-visible {
    background-color: var(--color-background-200);
  }

  &[aria-current="true"] {
    background-color: var(--color-background-300);
    color: var(--color-foreground-800);

    &:hover, &:focus-visible {
      background-color: var(--color-background-200);
    }
  }
}
</style>
